import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, UrlTree } from '@angular/router';
import {
  BreakpointService,
  DataDmAttributeDirective,
  MousedownDirective,
  QaHooksDirective,
  removeHtmlTagsFromString,
  UrlSafePipe,
  WindowRef,
} from '@boels/helpers';
import {
  BOProduct,
  ProductSearchPage,
  Suggestion,
  SuggestionResponse,
} from '@boels/models';
import {
  CmsRouterLinkDirective,
  CultureFacade,
  GlobalProductSearchFacade,
} from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleXmark, faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { CustomSearchBoxService } from './custom-searchbox.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProductIdentifierComponent } from '@boels/elements';
import { MediaComponent } from '../media/media.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'boels-custom-searchbox',
  templateUrl: './custom-searchbox.component.html',
  styleUrls: ['./custom-searchbox.component.scss'],
  providers: [
    CustomSearchBoxService,
    GlobalProductSearchFacade,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['homepageSearch', 'productDetails'],
    },
  ],
  imports: [
    CommonModule,
    FontAwesomeModule,
    FormsModule,
    MediaComponent,
    CmsRouterLinkDirective,
    TranslocoModule,
    ProductIdentifierComponent,
    DataDmAttributeDirective,
    QaHooksDirective,
    ButtonComponent,
    MousedownDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomSearchBoxComponent implements OnDestroy, OnInit, OnChanges {
  #breakpointService = inject(BreakpointService);
  #cultureFacade = inject(CultureFacade);
  #customSearchBoxService = inject(CustomSearchBoxService);
  #router = inject(Router);
  #translocoService = inject(TranslocoService);
  #urlSafePipe = inject(UrlSafePipe);
  #winRef = inject(WindowRef);
  #destroyRef = inject(DestroyRef);

  isHomepage = input(false);
  doSearch = input(false);
  searchQueryValue = model<string>('');

  searchQueryValueChanged = output<string>();
  searchResultsChanged = output<boolean>();

  @ViewChild('searchInput') searchInput: ElementRef;

  protected readonly isMobile = toSignal(this.#breakpointService.mdAndBelow$);

  search$ = new BehaviorSubject('');
  destroy$ = new Subject<void>();

  faCircleXmark = faCircleXmark;
  faSearch = faSearch;

  showResultsPreview = signal(false);
  navigatedToSearchResults = signal(false);
  isSearchValueEmpty = signal(true);
  activeLanguage = signal('');
  resultsDisplayedForSearchedValue = signal('');
  previousSearchUrl = signal<UrlTree | undefined>(undefined);
  suggestions = signal<Suggestion[]>([]);
  products = signal<BOProduct[]>([]);
  activeSearchResultIndex = signal(-1);
  timeoutId: number | undefined;

  public ngOnInit(): void {
    const savedSearchValue = this.#customSearchBoxService.getSearchValue();
    if (savedSearchValue && !this.isHomepage()) {
      this.searchQueryValue.set(savedSearchValue);
      this.#customSearchBoxService.setSearchValue('');
      this.showResultsPreview.set(false);
    }

    this.#cultureFacade
      .getActiveLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        if (
          this.activeLanguage() !== '' &&
          this.activeLanguage() !== language &&
          this.searchQueryValue?.length > 0
        ) {
          this.searchForItems(this.searchQueryValue(), false);
        }
        this.activeLanguage.set(language);
      });

    this.search$
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$),
        filter((searchTerm) => !!searchTerm),
      )
      .subscribe((searchTerm: string) => {
        const openResults = !savedSearchValue;
        this.searchForItems(searchTerm, openResults);
      });

    this.search$.next(this.searchQueryValue());
    this.#destroyRef.onDestroy(() => {
      if (this.timeoutId) clearTimeout(this.timeoutId);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['doSearch']?.currentValue) {
      this.onNavigateToSearchResults(this.searchQueryValue());
    }
  }

  updateSearchBoxValue(searchTerm: string): void {
    this.searchQueryValue.set(searchTerm);
    if (searchTerm.trim().length === 0) {
      this.isSearchValueEmpty.set(true);
      this.suggestions.set([]);
      this.products.set([]);
    }

    this.activeSearchResultIndex.set(-1);

    this.searchQueryValueChanged.emit(searchTerm);
    this.search$.next(searchTerm);
  }

  clearSearchBoxValue(): void {
    this.searchInput.nativeElement.focus();
    this.updateSearchBoxValue('');
  }

  public onFocusOut(): void {
    this.timeoutId = this.#winRef.setTimeout(() => {
      this.showResultsPreview.set(false);
    }, 200);
  }

  /**
   * Function to handle the keydown event on the search input for keyboard accessibility
   * ArrowDown: Move to the next search result
   * ArrowUp: Move to the previous search result
   * Enter: Navigate to the selected search result or search query value
   * @param event KeyboardEvent
   * @param searchQueryValue string
   * */
  onKeyDown(event: KeyboardEvent, searchQueryValue: string): void {
    const totalResults = this.suggestions().length + this.products().length;
    switch (event.key) {
      case 'ArrowDown':
        this.activeSearchResultIndex.set(
          (this.activeSearchResultIndex() + 1) % totalResults,
        );
        break;
      case 'ArrowUp':
        this.activeSearchResultIndex.set(
          (this.activeSearchResultIndex() - 1 + totalResults) % totalResults,
        );
        break;
      case 'Enter':
        // eslint-disable-next-line no-case-declarations
        const searchItem = [...this.suggestions(), ...this.products()][
          this.activeSearchResultIndex()
        ] as Suggestion | BOProduct;

        if (searchItem) {
          if ('value' in searchItem) {
            this.onNavigateToSearchResults(searchItem.value as string);
          } else {
            this.setInputValue(searchItem as BOProduct);
            this.goToProductPage((searchItem as BOProduct).url as string);
          }
        } else {
          this.updateSearchBoxValue(searchQueryValue);
          this.onNavigateToSearchResults(searchQueryValue);
        }
        break;
    }

    const activeElement = document.getElementById(
      `suggestion-${this.activeSearchResultIndex()}`,
    );
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  goToProductPage(commands: string): void {
    this.#cultureFacade
      .getActiveCulture()
      .pipe(filter(Boolean), take(1))
      .subscribe((culture) => {
        this.#router.navigate([
          '/' + culture,
          ...commands
            .split('/')
            .filter((value: string) => value.length > 0)
            .map((value: string) => this.#urlSafePipe.transform(value)),
        ]);
      });
  }

  hasResults(): boolean {
    const hasInputValue = !!this.searchQueryValue();
    const hasSuggestions = !!this.suggestions();
    const hasProducts = !!this.products();
    const shouldShowResults =
      this.showResultsPreview() &&
      !this.isSearchValueEmpty() &&
      !this.navigatedToSearchResults();
    const hasResults =
      shouldShowResults && hasInputValue && hasSuggestions && hasProducts;
    this.searchResultsChanged.emit(hasResults);
    return shouldShowResults && hasInputValue && hasSuggestions && hasProducts;
  }

  public setInputValue(product: BOProduct) {
    this.searchQueryValue.set(removeHtmlTagsFromString(product.name ?? ''));
    this.searchInput.nativeElement.blur();
  }

  public onNavigateToSearchResults(searchTerm = '') {
    // 1. Check if the search term is empty
    if (searchTerm === '') return;

    // 2. Check if the search term is the same as the previous search term
    // and if the previous search url is not undefined
    if (
      searchTerm === this.resultsDisplayedForSearchedValue() &&
      this.previousSearchUrl() !== undefined
    ) {
      // 3. Check if the current URL is the same as the previous search URL if so do nothing
      if (this.#router.url === this.previousSearchUrl()?.toString()) return;

      // 4. Navigate to the previous search URL
      this.#router.navigateByUrl(this.previousSearchUrl() as UrlTree);
      return;
    }

    this.navigatedToSearchResults.set(true);
    this.showResultsPreview.set(false);
    this.searchQueryValue.set(searchTerm);
    this.searchInput.nativeElement.focus();
    this.searchInput.nativeElement.blur();

    this.#cultureFacade
      .getActiveCulture()
      .pipe(take(1))
      .subscribe((culture: string) => {
        const url = this.#router.createUrlTree(
          ['', culture, 'search', 'find'],
          {
            queryParams: { query: searchTerm },
          },
        );
        if (this.isHomepage()) {
          this.#customSearchBoxService.setSearchValue(this.searchQueryValue());
        }
        this.resultsDisplayedForSearchedValue.set(searchTerm);
        this.previousSearchUrl.set(url);
        this.#router.navigateByUrl(url);
      });
  }

  private searchForItems(searchTerm: string, openResults = true): void {
    this.#customSearchBoxService
      .getSuggestions(searchTerm)
      .pipe(take(1))
      .subscribe((result: SuggestionResponse) => {
        this.suggestions.set(result.suggestions);
        this.isSearchValueEmpty.set(false);
        this.showResultsPreview.set(openResults);
      });

    this.#customSearchBoxService
      .getProducts(searchTerm, 5)
      .pipe(take(1))
      .subscribe((result: ProductSearchPage) => {
        this.products.set(result.products ?? []);
        if (
          this.suggestions &&
          this.suggestions.length === 0 &&
          this.products.length > 0
        ) {
          this.suggestions.update((suggestions) => [
            ...suggestions,
            { value: this.searchQueryValue() },
          ]);
        }
        this.addUrlsToProducts();
        this.isSearchValueEmpty.set(false);
        this.showResultsPreview.set(openResults);
      });
  }

  private addUrlsToProducts(): void {
    this.products.set(
      this.products().map((product) => {
        const name = removeHtmlTagsFromString(product.name ?? '');
        const hire = this.#urlSafePipe.transform(
          this.#translocoService.translate('productDetails.rental'),
        );
        return {
          ...product,
          url: `${hire}/${this.#urlSafePipe.transform(name)}/p/${product.code}`,
        };
      }),
    );
  }

  /**
   * Function to handle onFocus event on the search input
   */
  public onFocusSearchInput() {
    const nativeWindow = this.#winRef.nativeWindow;

    // Only scroll if the view is mobile
    if (nativeWindow && this.isMobile()) {
      // Calculate the offset of the search input
      const nativeElement = this.searchInput.nativeElement;
      if (!nativeElement) return;

      const offset =
        this.searchInput.nativeElement.getBoundingClientRect().top +
        nativeWindow.scrollY -
        24;

      setTimeout(() => {
        // Only scroll if the search input is not already at the top
        if (nativeWindow.scrollY !== offset) {
          nativeWindow.scrollTo({ top: offset, behavior: 'smooth' });
        }
      }, 250);
    }
  }

  shouldShowResultsPreview(): boolean {
    return (
      !!this.showResultsPreview() &&
      !this.navigatedToSearchResults() &&
      !!this.searchQueryValue() &&
      ((this.suggestions() && this.suggestions().length > 0) ||
        (this.products() && this.products().length > 0))
    );
  }

  shouldShowNoResults(): boolean {
    return (
      !!this.searchQueryValue() &&
      !!this.showResultsPreview() &&
      !this.isSearchValueEmpty() &&
      !this.navigatedToSearchResults() &&
      !!this.products() &&
      this.products().length === 0 &&
      !!this.suggestions() &&
      this.suggestions().length === 0
    );
  }
}
